/**
 * Phira's cubic-bezier easing (`bezier != 0`, `bezierPoints = [x1, y1, x2, y2]`, F§6.7, R§2.1), reimplemented
 * from the algorithm described in rpe-format.md F§6.7 (a variant of gre/bezier-easing; no Phira code copied):
 *
 *  - polynomial coefficients `a = (a1 - a2) * 3 + 1`, `b = a2 * 3 - a1 * 6`, `c = a1 * 3`,
 *    `sample(t) = ((a t + b) t + c) t`, `slope(t) = (a * 3 * t + b * 2) t + c`, every step in f32;
 *  - a 21-entry table of x(t) at t = i / 20;
 *  - x = 0 or x = 1 returns t = x exactly;
 *  - the table segment is picked by `(x / STEP) as usize` (Rust saturating cast: negative and NaN -> 0), capped
 *    at 20; the initial guess interpolates linearly inside that segment;
 *  - slope <= 1e-7: the guess is returned; slope >= 1e-3: 4 Newton iterations (stopping when the slope drops
 *    to <= 1e-7); otherwise up to 10 bisection steps inside the segment (stopping when |x(t) - x| <= 1e-7).
 *
 * x > 1 makes Phira index the table past its end (a panic). It can only be reached through overlapping, unsorted
 * or negative-length event lists; this port returns NaN there (test/refEval.ts raises RefPanic instead).
 *
 * The f64 geometry helpers at the bottom (exact curve points, precise x -> t) serve splitting, overshoot
 * detection and fitting; they are not part of Phira's evaluation.
 */

import type { BezierPoints } from './types';

const fr = Math.fround;

const TABLE_SIZE = 21;
const STEP = fr(1 / (TABLE_SIZE - 1));
const MIN_SLOPE = fr(1e-7);
const NEWTON_MIN_SLOPE = fr(1e-3);
const NEWTON_ITERATIONS = 4;
const BISECTION_ITERATIONS = 10;
const BISECTION_PRECISION = fr(1e-7);

interface Coeffs {
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

function coeffs(a1: number, a2: number): Coeffs {
  return { a: fr(fr(fr(a1 - a2) * 3) + 1), b: fr(fr(a2 * 3) - fr(a1 * 6)), c: fr(a1 * 3) };
}

function sample(k: Coeffs, t: number): number {
  return fr(fr(fr(fr(fr(k.a * t) + k.b) * t) + k.c) * t);
}

function slope(k: Coeffs, t: number): number {
  return fr(fr(fr(fr(fr(k.a * 3) * t) + fr(k.b * 2)) * t) + k.c);
}

/** Rust `f32 as usize`: saturating, NaN -> 0. */
function asUsize(v: number): number {
  if (Number.isNaN(v) || v <= 0) return 0;
  return v >= Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : Math.trunc(v);
}

/** A prepared Phira bezier curve (the table is built once; reuse it in hot loops). */
export interface BezierCurve {
  /** Control points as Phira stores them (f32). */
  readonly p: BezierPoints;
  /** Phira's x -> t solver (f32). */
  tForX(x: number): number;
  /** Phira's eased value y(x) (f32). */
  y(x: number): number;
}

export function createBezierCurve(p: BezierPoints): BezierCurve {
  const x1 = fr(p[0]);
  const y1 = fr(p[1]);
  const x2 = fr(p[2]);
  const y2 = fr(p[3]);
  const kx = coeffs(x1, x2);
  const ky = coeffs(y1, y2);
  const table: number[] = [];
  for (let i = 0; i < TABLE_SIZE; i++) table.push(sample(kx, fr(i * STEP)));

  const tForX = (xIn: number): number => {
    const x = fr(xIn);
    if (x === 0 || x === 1) return x;
    const id = Math.min(asUsize(fr(x / STEP)), TABLE_SIZE - 1);
    const lo = table[id];
    const hi = table[id + 1];
    if (lo === undefined || hi === undefined) return Number.NaN;
    const dist = fr(fr(x - lo) / fr(hi - lo));
    const initT = fr(STEP * fr(id + dist));
    const s0 = slope(kx, initT);
    if (s0 <= MIN_SLOPE) return initT;
    if (s0 >= NEWTON_MIN_SLOPE) {
      let t = initT;
      for (let i = 0; i < NEWTON_ITERATIONS; i++) {
        const s = slope(kx, t);
        if (s <= MIN_SLOPE) return t;
        t = fr(t - fr(fr(sample(kx, t) - x) / s));
      }
      return t;
    }
    let l = fr(STEP * id);
    let r = fr(STEP * fr(id + 1));
    let t = fr(fr(l + r) / 2);
    for (let i = 0; i < BISECTION_ITERATIONS; i++) {
      const d = fr(sample(kx, t) - x);
      if (Math.abs(d) <= BISECTION_PRECISION) break;
      if (d > 0) r = t;
      else l = t;
      t = fr(fr(l + r) / 2);
    }
    return t;
  };

  return {
    p: [x1, y1, x2, y2],
    tForX,
    y: (x) => sample(ky, tForX(x)),
  };
}

/** Phira's bezier easing y(x) in f32 (builds the 21-sample table on every call; see createBezierCurve). */
export function bezierY(p: BezierPoints, x: number): number {
  return createBezierCurve(p).y(x);
}

// ---------------------------------------------------------------------------------------------------------------
// f64 geometry (exact curve, used by split / overshoot detection / fitting; not Phira's evaluation)
// ---------------------------------------------------------------------------------------------------------------

/** Cubic bezier coordinate from 0 to 1 with inner control values c1, c2 at parameter t (f64). */
export function bezierCoord(c1: number, c2: number, t: number): number {
  const u = 1 - t;
  return 3 * u * u * t * c1 + 3 * u * t * t * c2 + t * t * t;
}

/** Point (x, y) of the curve at parameter t (f64). */
export function bezierPointAt(p: BezierPoints, t: number): readonly [number, number] {
  return [bezierCoord(p[0], p[2], t), bezierCoord(p[1], p[3], t)];
}

/**
 * Precise parameter t in [0, 1] where x(t) = x (f64). For curves with x1, x2 in [0, 1] x(t) is monotone and the
 * root is unique; otherwise the root closest to Phira's own solution is returned.
 */
export function solveBezierT(p: BezierPoints, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  const [x1, , x2] = p;
  const f = (t: number): number => bezierCoord(x1, x2, t) - x;
  const monotone = x1 >= 0 && x1 <= 1 && x2 >= 0 && x2 <= 1;
  if (monotone) return bisect(f, 0, 1);
  const roots: number[] = [];
  const steps = 512;
  let prevT = 0;
  let prevF = f(0);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const v = f(t);
    if (prevF === 0) roots.push(prevT);
    else if (prevF < 0 !== v < 0 && v !== 0) roots.push(bisect(f, prevT, t));
    prevT = t;
    prevF = v;
  }
  if (prevF === 0) roots.push(1);
  const guess = createBezierCurve(p).tForX(x);
  let best = roots[0] ?? guess;
  for (const r of roots) if (Math.abs(r - guess) < Math.abs(best - guess)) best = r;
  return best;
}

function bisect(f: (t: number) => number, lo: number, hi: number): number {
  let a = lo;
  let b = hi;
  const fa = f(a);
  for (let i = 0; i < 200; i++) {
    const m = (a + b) / 2;
    if (m <= a || m >= b) break;
    const fm = f(m);
    if (fm === 0) return m;
    if (fm < 0 === fa < 0) a = m;
    else b = m;
  }
  return (a + b) / 2;
}
